import { Component, OnInit, Input } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import { NzModalRef, NzModalService } from "ng-zorro-antd/modal";
import { NzMessageService } from "ng-zorro-antd/message";
import { differenceInCalendarDays } from "date-fns";
import * as moment from "moment";
import _ from "lodash";

import { HouseStatusSettingService } from "@root/router/setting/custom-config/house-status-setting.service";
import { NoteV2Service } from "@root/router/component/note-v2/note-v2.service";
import { TranslateService } from "@root/shared/service/translate.service";
import { WritePermissionGurd } from "@root/util/decorator";
import { DateService } from "@root/shared/service/date.service";
import { InternationalService } from "@root/international.service";
// import { IntlTimeFormat } from '@root/config';

enum TYPE {
  INCOME = 1,
  COST = -1,
}

interface File {
  url: string; // 图片url
  id: number; // 凭证id
}

type Share = "no" | "month";

@Component({
  selector: "host-note-v2",
  templateUrl: "./note-v2.component.html",
  styleUrls: ["./note-v2.component.scss"],
})
export class NoteV2Component implements OnInit {
  readonly FeeType: typeof TYPE = TYPE;
  @Input() type: "edit" | "create" = "create";
  @Input() scope: "order" | "global" = "global";
  @Input() data;
  @Input() id;
  @Input() orderCode;

  private readonly dateFormatKey = "hostex-i18n-dateformat";
  private readonly timeFormatKey = "hostex-i18n-timeformat";

  listOfOutcomeItem = [];
  listOfOutcomeMethod = [];
  listOfIncomeItem = [];
  listOfIncomeMethod = [];
  listOfDayOption = _.range(1, 29).map((i) => ({
    label: this.t.t("component.note.day_in_month", { i }),
    value: i < 10 ? `0${i}` : i,
  }));
  currencyList = ["CNY"];

  form: UntypedFormGroup = this.fb.group({
    type: [TYPE.INCOME, Validators.required],
    action_type: [22, Validators.required],
    currency_type: [this.internationalService.getCurrency() || "CNY", Validators.required],
    amount: [null, Validators.required],
    action_date: null,
    payment_method: [null, Validators.required],
    house_ids: null,
    invoice_ids: null,
    share: "no",
    month_share: null,
    // shareChecked: 0,
    action_day: "01",
    action_time: null,
    note: null,
  });

  checking = false;
  defaultOpenValue = new Date(0, 0, 0, 0, 0, 0);

  // /**
  //  * @deprecated
  //  */
  // currency = this.internationalService.getCurrency() || 'CNY';

  angularDateFormat = this.dateService.getAngularFormat(["YMD", "hms"]);
  angularTimeFormat = this.dateService.getAngularFormat([null, "hms"]);

  get isIncomeType() {
    return this.form.get("type").value === TYPE.INCOME;
  }

  get isOutcomeType() {
    return this.form.get("type").value === TYPE.COST;
  }

  get isShareMonth() {
    return this.form.get("share").value === "month";
  }

  // get share(): Share {
  //   return this.form.get("share").value;
  // }

  get listOfItemOption() {
    if (this.isIncomeType) {
      return this.listOfIncomeItem;
    } else {
      return this.listOfOutcomeItem;
    }
  }

  get listOfMethodOption() {
    if (this.isIncomeType) {
      return this.listOfIncomeMethod;
    } else {
      return this.listOfOutcomeMethod;
    }
  }

  get amountPlaceHolder() {
    return this.isIncomeType ? this.t.t("component.note.income_amount") : this.t.t("component.note.outcome_amount");
  }
  get use12Hours() {
    return this.dateService.use12hr;
    // const timeFormat = this.dateService.getTimeFormat();
    // return timeFormat === IntlTimeFormat.Twelve ? true : false;
  }

  get currencyType() {
    return this.form.get("currency_type").value;
  }

  // disabledTime = (current: Date): boolean => {
  //   const today = new Date();
  //   const diff = differenceInCalendarDays(current, today);
  //   return diff > 365 || diff < -365;
  // };

  constructor(
    private fb: UntypedFormBuilder,
    private subject: NzModalRef,
    private noteService: NoteV2Service,
    private commonConfigService: HouseStatusSettingService,
    private messageService: NzMessageService,
    private t: TranslateService,
    private dateService: DateService,
    private internationalService: InternationalService,
  ) {}

  ngOnInit() {
    this.patchForm();
    this.fetchCommonConfig();
    // this.getCurrencyForOrder();
    this.getCurrencyList();

    this.subscribeFormChange();
  }

  private subscribeFormChange() {
    this.form.get("share").valueChanges.subscribe((rst) => {
      if (rst === "month") {
        this.form.patchValue({
          action_time: moment().set({ hour: 0, minute: 0, second: 0 }).toDate(),
        });
      }
    });
  }

  patchForm() {
    if (this.type === "edit") {
      console.log(this.data);
      const date = _.get(this.data, "action_date");
      this.form.patchValue({
        type: this.data.type === 0 ? -1 : this.data.type,
        action_type: this.data.action_type,
        currency_type: this.data.currency_type,
        amount: this.data.amount,
        action_date: moment(date).toDate(),
        payment_method: this.data.payment_method,
        month_share: null,
        note: this.data.note,
        house_ids: this.data.house_ids,
        invoice_ids: this.data.invoice,
      });
    } else {
      this.form.patchValue({
        action_date: moment().toDate(),
        ...this.data
      });
    }
  }

  fetchCommonConfig() {
    this.commonConfigService.getSettings().subscribe((res) => {
      const filter = (list) => list.filter((item) => item.delete_time === null);
      const map = (list, label, value) => list.map((item) => ({ label: item[label], value: item[value] }));

      this.listOfIncomeMethod = map(filter(res?.data?.income_method ?? []), "method_name", "code");
      this.listOfOutcomeMethod = map(filter(res?.data?.outcome_method ?? []), "method_name", "code");
      this.listOfIncomeItem = map(filter(res?.data?.income_item ?? []), "item_name", "code");
      this.listOfOutcomeItem = map(filter(res?.data?.outcome_item ?? []), "item_name", "code");

      if (this.type === "create") {
        this.setActionTypeDefaultValue();
      }
    });
  }

  getCurrencyForOrder() {
    // if (this.data) {
    //   this.currency = _.get(this.data, 'currency_type') || _.get(this.data, 'currency');
    //   return;
    // }
    // if (this.orderDetailCurrency) {
    //   this.currency = this.orderDetailCurrency;
    // }
  }

  getCurrencyList() {
    this.noteService.getCurrency().subscribe((res) => {
      if (res.error_code === 0) {
        this.currencyList = _.get(res, "data.currency_list");
      }
    });
  }

  private setActionTypeDefaultValue() {
    if (this.isIncomeType) {
      // income
      this.form.patchValue({ action_type: this.listOfIncomeItem?.[0]?.value });
    } else {
      // outcome
      this.form.patchValue({ action_type: this.listOfOutcomeItem?.[0]?.value });
    }
  }

  typeChange() {
    this.setActionTypeDefaultValue();
    const value = this.form.get("type").value;
    // 记账，收入默认房费22，支出默认人工费用1
    this.form.patchValue({
      // action_type: value === 1 ? 22 : 1,
      house_ids: null,
    });
  }

  onConfirm() {
    this.updateTransaction();
  }

  @WritePermissionGurd("a_balance_of_payments")
  updateTransaction() {
    this.checking = true;
    const valid = this.checkFormValid();
    if (!valid) {
      return;
    }
    if (this.type === "edit") {
      // if (this.orderOrWhole === 'whole') {
      if (this.scope === "global") {
        this.requestUpdateTransaction();
      } else {
        this.requestUpdateOrderTransaction();
      }
    } else {
      // if (this.orderOrWhole === 'whole') {
      if (this.scope === "global") {
        this.requestCreateTransaction();
      } else {
        this.requestCreateOrderTransaction();
      }
    }
  }

  requestUpdateTransaction() {
    const value = _.omit(this.form.value, ["share"]);
    if (value.house_ids && !Array.isArray(value.house_ids)) {
      value.house_ids = [value.house_ids];
    }

    const params = {
      ...value,
      id: this.id,
      invoice_ids: value.invoice_ids?.map((i) => i.id),
      action_date: moment(value.action_date).format("YYYY-MM-DD HH:mm:ss"),
    };

    this.noteService.modifyTransaction(params).subscribe((res) => {
      if (res.error_code === 0) {
        const successText = this.t.t("success.modify_note");
        this.messageService.success(successText);
        this.subject.destroy({
          refresh: true,
        });
      }
    });
  }

  requestCreateTransaction() {
    const value = _.omit(this.form.value, ["share"]);
    if (value.house_ids && !Array.isArray(value.house_ids)) {
      value.house_ids = [value.house_ids];
    }
    const params = {
      ...value,
      invoice_ids: value.invoice_ids?.map((i) => i.id),
    };
    if (this.isIncomeType || !this.isShareMonth) {
      Object.assign(params, {
        month_share: null,
        action_date: moment(value.action_date).format("YYYY-MM-DD HH:mm:ss"),
      });
    } else {
      Object.assign(params, {
        action_date: `${value.action_day} ${moment(value.action_time).format("HH:mm:ss")}`,
        month_share: value.month_share?.map((d) => moment(d).format("YYYY-MM-DD")),
      });
    }

    this.noteService.addNoteV2(params).subscribe((res) => {
      if (res?.error_code === 0) {
        // this.noteService.addNoteChange("true");
        const successtext = this.t.t("success.add_note");
        this.messageService.success(successtext);
        this.subject.destroy();
      }
    });
  }

  requestUpdateOrderTransaction() {
    const value = _.omit(this.form.value, ["share"]);
    // const value = this.form.value;
    const params = {
      ...value,
      format: "v_2",
      transaction_id: this.id,
      invoice_ids: value.invoice_ids?.map((i) => i.id),
      action_time: moment(value.action_date).format("YYYY-MM-DD HH:mm:ss"),
    };

    this.noteService.modifyReservationNote(params).subscribe((res) => {
      if (res.error_code === 0) {
        this.subject.destroy({
          refresh: true,
        });
      }
    });
  }

  requestCreateOrderTransaction() {
    const value = _.omit(this.form.value, ["share"]);

    const params = {
      ...value,
      reservation_code: this.orderCode,
      format: "v_2",
      invoice_ids: value.invoice_ids?.map((i) => i.id),
      action_time: moment(value.action_date).format("YYYY-MM-DD HH:mm:ss"),
    };

    this.noteService.addReservationNote(params).subscribe((res) => {
      if (res.error_code === 0) {
        this.subject.destroy({
          refresh: true,
        });
      }
    });
  }

  checkFormValid() {
    const value = this.form.value;
    const amount = value.amount;
    if (!amount || value.amount === "") {
      return false;
    }
    if (this.isShareMonth && (!value.month_share || !value.action_time)) {
      return false;
    }
    if (value.payment_method === null) {
      return false;
    }
    return true;
  }

  cancel() {
    this.subject.destroy("onCancel");
  }
}
